import React, { useContext, useEffect, useMemo, useState } from 'react';
import { DndContextWrapper } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/DragAndDrop';
import { remCalc } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import useActivityAudio from '@monorepo/shared/src/hooks/use-activity-audio';
import { Box, Paper, Theme, useMediaQuery } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  DndDragBox,
  DndDropBox,
} from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/DragAndDrop';
import GWDragAndDrop from 'assets/images/drag-and-drop.png';
import { ActivityContext } from 'state/providers/ActivityProvider';
import type { GuessTheOrderStoryElement } from '@monorepo/shared/src/graph/schema-generated-types-student';
import { DndDragEndEvent, GuessStates } from 'types';
import GTheOrderRoundLayout from './GTheOrderRoundLayout';
import ActivityPlayButtonOverlay from '../../components/ActivityPlayButtonOverlay';
import clsx from 'clsx';
import { InstructionButtonContext } from 'state/providers/InstructionsButtonProvider';
import AspectRatioWrapper from '@monorepo/shared/src/components/common/AspectRatioWrapper';
import { getCorrectGuessStyle } from '../../util';
interface SceneState {
  storyOrder: string;
  order: number;
  active: boolean;
  correct: boolean;
}

const sceneOrderMap = {
  ['BEGINNING']: 1,
  ['MIDDLE']: 2,
  ['END']: 3,
};

const sortSceneByStoryOrder = (a: GuessTheOrderStoryElement, b: GuessTheOrderStoryElement) => {
  a = sceneOrderMap[a.tile.storyOrder as string];
  b = sceneOrderMap[b.tile.storyOrder as string];

  if (a === b) return 0;
  return a < b ? -1 : 1;
};

const t = (key: string, options?: any) => translate('activities', key, options);

const GTheOrderDndRound: React.FC = () => {
  const [{ sounds, isPlaying }, { playSoundFromState }] = useActivityAudio();
  const [state, { setIsActivityOver, setShowNextButton }] = useContext(ActivityContext);
  const [{ playInstructionFlag, currentInstruction }, { stopInstructions, setCurrentInstruction }] =
    useContext(InstructionButtonContext);

  const [guessIndicator, setGuessIndicator] = useState<{ id: string | null; status: string }>({
    id: null,
    status: GuessStates.NULL,
  });
  const [showActivityPlayButton, setShowActivityPlayButton] = useState<boolean>(true);
  const initialSceneState: SceneState[] = useMemo(
    () =>
      Object.keys(sceneOrderMap).map((element) => {
        const round: SceneState = {
          storyOrder: element,
          active: sceneOrderMap[element] === 1,
          correct: false,
          order: sceneOrderMap[element],
        };
        return round;
      }),
    [sceneOrderMap]
  );
  const [sceneState, setSceneState] = useState<Array<SceneState>>(initialSceneState);

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.between('xs', 'md'));
  const classes = styles();

  const handleOnDragEnd = (event: DndDragEndEvent) => {
    if (event.over && event.active.id === event.over.id) {
      onTileDropSuccess(event.active.id as string);
    }

    if (event.over && event.active.id !== event.over.id) {
      onTileDropError(event.over.id as string);
    }
  };

  const getSceneStateByName = (storyOrder) => {
    return sceneState.find((x) => x.storyOrder == storyOrder);
  };

  const maybeEndActivity = async () => {
    if (sceneState.every((scene) => scene.correct)) {
      await setIsActivityOver(true);
      await playSoundFromState(sounds['summary'], true);
      setShowNextButton(true);
    }
  };

  const playSuccessAudio = async (storyOrder) => {
    await playSoundFromState(sounds[`${storyOrder}-affirmation`], true);
    const nextAudio = sceneState.find((x) => x.active)?.storyOrder ?? '';
    setCurrentInstruction(nextAudio);
    await playSoundFromState(sounds[nextAudio], true);
    await maybeEndActivity();
  };

  const onTileDropSuccess = async (id: string) => {
    const storyOrder = sceneState.find((x) => x.active)?.storyOrder;
    playSuccessAudio(storyOrder);
    const nextState = [...sceneState];
    const updatedSceneIndex = nextState.findIndex((x) => x.active);
    nextState[updatedSceneIndex].correct = true;
    nextState[updatedSceneIndex].active = false;
    const nextIndex = nextState.findIndex(
      (x) => x.order === nextState[updatedSceneIndex].order + 1
    );
    if (nextIndex >= 0) {
      nextState[nextIndex].active = true;
    }
    setSceneState(nextState);
    setGuessIndicator(() => ({ id, status: GuessStates.SUCCESS }));
    setTimeout(() => {
      setGuessIndicator(() => ({ id, status: GuessStates.NULL }));
    }, 500);
  };

  const onTileDropError = (id: string) => {
    playSoundFromState(sounds['boink'], true);
    setGuessIndicator(() => ({ id, status: GuessStates.ERROR }));
    setTimeout(() => {
      setGuessIndicator(() => ({ id, status: GuessStates.NULL }));
    }, 500);
  };

  const onActivityButtonClick = () => {
    setShowActivityPlayButton(false);
    const playIntroAudio = async () => {
      await playSoundFromState(sounds['intro'], true);
      !isPlaying && playSoundFromState(sounds['BEGINNING']);
    };
    if (sounds['intro'] && sounds['BEGINNING']) {
      setCurrentInstruction('intro');
      playIntroAudio();
    }
  };

  const sceneComponents = useMemo(() => {
    const aspectRatio = 760 / 810; // image width:height
    return (state?.currentRound?.data?.tiles || [])
      .slice()
      .sort(sortSceneByStoryOrder)
      .map((data) => ({
        label: data.tile.storyOrder,
        data: getSceneStateByName(data.tile.storyOrder)?.correct
          ? () => (
              <AspectRatioWrapper ratio={aspectRatio}>
                <Paper className={classes.dndDropBox} elevation={4} key={data.tile.description}>
                  <img
                    alt={t('gwDragAndDrop')}
                    className={classes.sceneImage}
                    src={data.scene.src}
                  />
                </Paper>
              </AspectRatioWrapper>
            )
          : () => (
              <DndDropBox
                key={data.tile.description}
                id={data.tile.storyOrder}
                disabled={!getSceneStateByName(data.tile.storyOrder)?.active}
              >
                <AspectRatioWrapper ratio={aspectRatio}>
                  <Paper
                    className={clsx(
                      classes.dndDropBox,
                      !getSceneStateByName(data.tile.storyOrder)?.active &&
                        !getSceneStateByName(data.tile.storyOrder)?.correct &&
                        classes.disabledBackground
                    )}
                    elevation={4}
                  >
                    {getSceneStateByName(data.tile.storyOrder)?.active ? (
                      <img
                        alt={t('gwSceneImgAlt', { alt: data.scene.description })}
                        className={classes.sceneImage}
                        src={GWDragAndDrop}
                      />
                    ) : null}
                  </Paper>
                </AspectRatioWrapper>
              </DndDropBox>
            ),
      }));
  }, [sceneState, isMobile]);

  const tileComponents = useMemo(
    () =>
      (state?.currentRound?.data?.tiles || []).map((data) => () => {
        const correctGuesses = sceneState?.map((scene) => {
          return scene.correct ? scene.storyOrder : '';
        });
        return (
          <DndDragBox
            isCorrectStyle={getCorrectGuessStyle(correctGuesses, data.tile.storyOrder)}
            key={data.tile.description}
            id={data.tile.storyOrder}
          >
            <Paper className={classes.tileBackground} elevation={4}>
              <img className={classes.tileImage} src={data.tile.src} alt="" />
            </Paper>
          </DndDragBox>
        );
      }),
    [sceneState]
  );

  useEffect(() => {
    const playSound = async (currentInstruction) => {
      await playSoundFromState(sounds[currentInstruction], true);
      if (currentInstruction === 'intro') {
        !isPlaying && (await playSoundFromState(sounds['BEGINNING']));
      }
      stopInstructions();
    };
    if (currentInstruction && playInstructionFlag) {
      playSound(currentInstruction);
    }
  }, [playInstructionFlag, currentInstruction]);

  return (
    <Box className={classes.gTheOrderRoundContainer}>
      <DndContextWrapper handleOnDragEnd={handleOnDragEnd as any}>
        <GTheOrderRoundLayout
          guessIndicator={guessIndicator}
          sceneComponents={sceneComponents}
          tileComponents={tileComponents}
        />
        {showActivityPlayButton && (
          <ActivityPlayButtonOverlay onOverlayClick={onActivityButtonClick} />
        )}
      </DndContextWrapper>
    </Box>
  );
};

const styles = makeStyles((theme) => ({
  dndDropBox: {
    alignItems: 'baseline',
    background: '#FBFBFB',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    overflow: 'hidden',
    width: '100%',
  },
  sceneImage: {
    height: '100%',
    width: '100%',
    objectFit: 'scale-down',
  },
  gTheOrderRoundContainer: {
    maxWidth: remCalc(1024),
    margin: 'auto',
    position: 'relative',
  },
  tileBackground: {
    display: 'block',
    height: '100%',
    width: '100%',
  },
  tileImage: {
    height: '100%',
    width: '100%',
    borderRadius: 4,
  },
  triggerBtn: {
    height: '0px',
    opacity: 0,
    visibility: 'hidden',
    weight: '0px',
  },
  disabledBackground: {
    background: theme.palette.grey[500],
  },
}));

export default GTheOrderDndRound;
